import type {
  CardContent,
  ChatCardTone,
  ConversationalFormOption,
  ChatMessage,
  ChatMessageContent,
  ChatMessageResponse,
  ChatMessageStatus,
} from '@karehero/models';
import { randomUUID } from '@karehero/helpers/string';

import type { BenefitsCalculatorAnswers } from 'config/benefitsCalculatorV2Steps';

export const message = (
  from: ChatMessage['from'],
  content: ChatMessage['content'],
): ChatMessage => ({ id: randomUUID(), from, content, status: 'sent' });

export const textMessage = (from: ChatMessage['from'], text: string) =>
  message(from, { kind: 'text', text });

/** The API keeps a sent attachment's document on the message, not its content. */
const contentOf = (msg: ChatMessageResponse): ChatMessageContent =>
  msg.documentId &&
  (msg.content.kind === 'voice' || msg.content.kind === 'file')
    ? { ...msg.content, documentId: msg.documentId }
    : msg.content;

/** Marks a handed-over file the server has finished with. */
const withProcessed = (
  content: ChatMessageContent,
  processedDocumentIds: string[] = [],
): ChatMessageContent =>
  content.kind === 'file' &&
  content.documentId &&
  processedDocumentIds.includes(content.documentId)
    ? { ...content, isProcessed: true }
    : content;

/** The API's status is open text; all but in flight and failed has landed. */
const statusOf = (status: string): ChatMessageStatus =>
  status === 'sending' || status === 'error' ? status : 'sent';

/** A saved message as the thread shows it; a file is marked once read. */
export const toChatMessage = (
  msg: ChatMessageResponse,
  processedDocumentIds: string[] = [],
): ChatMessage => ({
  id: msg.id,
  from: msg.senderKind,
  content: withProcessed(contentOf(msg), processedDocumentIds),
  status: statusOf(msg.status),
});

export const toApiMessage = (msg: ChatMessage): ChatMessageResponse => ({
  id: msg.id,
  sessionId: '',
  senderKind: msg.from,
  contentKind: msg.content.kind,
  content: msg.content,
  documentId: 'documentId' in msg.content ? msg.content.documentId : undefined,
  status: msg.status,
  createdAt: new Date().toISOString(),
});

/** A line of Keni's. */
export const say = (text: string) => textMessage('system', text);

/** A card of Keni's: lines, or a checklist in their place. */
export const card = (
  tone: ChatCardTone,
  title: string,
  lines: string[],
  extra: Pick<CardContent, 'items' | 'isEditable'> = {},
) => message('system', { kind: 'card', tone, title, lines, ...extra });

/** A question of Keni's with its answers attached under it. */
export const menu = (text: string, options: ConversationalFormOption[]) =>
  message('system', { kind: 'menu', text, options });

/** The latest card of a tone; a reverse loop, as findLast only arrived in iOS 15.4. */
export const lastCardID = (
  messages: readonly ChatMessageResponse[],
  tone: ChatCardTone,
): string | undefined => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const { content } = messages[i];
    if (content.kind === 'card' && content.tone === tone) return messages[i].id;
  }
  return undefined;
};

/** The answers a session's answers messages hold, by question; a later answer replaces an earlier one. */
export const answersFrom = (
  messages: readonly ChatMessageResponse[],
): BenefitsCalculatorAnswers => {
  const answers: BenefitsCalculatorAnswers = {};
  for (const msg of messages) {
    if (msg.content.kind !== 'answers' || msg.status === 'error') continue;
    for (const answer of msg.content.answers) {
      // The calculator's questions are all answered by options.
      answers[answer.questionId] = answer.optionIds ?? [];
    }
  }
  return answers;
};

/** What the server has finished reading, by document and by message. */
export interface Processed {
  documentIDs: readonly string[];
  messageIDs: readonly string[];
}

/** Whether a message has been read; one that failed to send has nothing to read. */
export const isMessageRead = (
  msg: ChatMessageResponse,
  processed: Processed,
): boolean => {
  if (msg.status === 'error') return true;
  // A voice note read from its transcript is read once that is, whatever its audio is doing.
  const isTranscribed =
    msg.content.kind === 'voice' && !!msg.content.transcript;
  return msg.documentId && !isTranscribed
    ? processed.documentIDs.includes(msg.documentId)
    : processed.messageIDs.includes(msg.id);
};

/** Whether a voice note is among the messages given. */
export const includesVoiceNote = (
  messages: readonly ChatMessageResponse[],
  ids: readonly string[] = [],
): boolean =>
  messages.some((m) => ids.includes(m.id) && m.content.kind === 'voice');
